import { Capacitor } from '@capacitor/core'

const ONLINE_PING_INTERVAL = 120000  // 2 minutes
const OFFLINE_PING_INTERVAL = 30000  // 30 seconds

class NetworkStatusService {
  constructor() {
    this._isOnline = navigator.onLine
    this._subscribers = new Set()
    this._pingTimer = null
    this._apiBaseUrl = ''

    // Listen to browser online/offline events
    window.addEventListener('online', () => this._handleChange(true))
    window.addEventListener('offline', () => this._handleChange(false))

    // Start periodic health check
    this._startPing()
  }

  get isOnline() {
    return this._isOnline
  }

  setApiBaseUrl(url) {
    this._apiBaseUrl = url
  }

  subscribe(callback) {
    this._subscribers.add(callback)
    return () => this._subscribers.delete(callback)
  }

  // Called by the API client when a request fails to reach the server. Flips to
  // offline immediately rather than waiting up to ONLINE_PING_INTERVAL for the
  // next health check to notice (a dropped connection or captive portal fires no
  // browser 'offline' event).
  markUnreachable() {
    this._handleChange(false)
  }

  _handleChange(online) {
    if (this._isOnline !== online) {
      this._isOnline = online
      this._subscribers.forEach(cb => cb(online))
      // Adjust ping frequency
      this._startPing()
    }
  }

  _startPing() {
    if (this._pingTimer) clearInterval(this._pingTimer)
    const interval = this._isOnline ? ONLINE_PING_INTERVAL : OFFLINE_PING_INTERVAL
    this._pingTimer = setInterval(() => this._ping(), interval)
  }

  async _ping() {
    try {
      const url = `${this._apiBaseUrl}/api/health`
      const res = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        signal: AbortSignal.timeout(5000)
      })
      this._handleChange(res.ok)
    } catch {
      this._handleChange(false)
    }
  }

  destroy() {
    if (this._pingTimer) clearInterval(this._pingTimer)
    this._subscribers.clear()
  }
}

// Singleton
export const networkStatus = new NetworkStatusService()
export default networkStatus
